import { useEffect, useMemo, useState } from 'react';

import { api } from './api';
import { CloseIcon, ReturnIcon } from './icons';
import { productSecondaryText } from './product-display';

interface ReturnLine {
  id: string;
  productName: string;
  detailName: string;
  unitName?: string;
  sku?: string;
  quantity: number;
  price: number;
  discountPercent?: number;
  isGift?: boolean;
}

interface ReturnDetailResponse {
  invoice: {
    id: string;
    sku: string;
    partnerName?: string;
    updatedAt: string;
    is_lock?: number;
    status?: number;
    vat?: number;
  };
  lines: ReturnLine[];
}

function money(value: number): string {
  return new Intl.NumberFormat('vi-VN').format(Number(value) || 0);
}

export function OutboundReturnModal({ invoiceId, onClose, onReturned }: {
  invoiceId: string;
  onClose: () => void;
  onReturned: (message: string) => void;
}) {
  const [data, setData] = useState<ReturnDetailResponse | null>(null);
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const controller = new AbortController();
    api<ReturnDetailResponse>(`/api/invoices/outbound/${invoiceId}`, {}, controller.signal)
      .then(setData)
      .catch((reason) => {
        if (!controller.signal.aborted) setError(reason instanceof Error ? reason.message : 'Không thể tải sản phẩm có thể trả.');
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [invoiceId]);

  useEffect(() => {
    const close = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !saving) onClose();
    };
    document.addEventListener('keydown', close);
    return () => document.removeEventListener('keydown', close);
  }, [onClose, saving]);

  const selected = useMemo(() => Object.values(quantities).filter((quantity) => quantity > 0).length, [quantities]);
  const estimatedRefund = useMemo(() => {
    const beforeVat = (data?.lines ?? []).reduce((sum, line) => {
      const quantity = quantities[line.id] ?? 0;
      if (line.isGift) return sum;
      return sum + Math.round(quantity * Number(line.price) * (1 - Number(line.discountPercent ?? 0) / 100));
    }, 0);
    return Math.round(beforeVat * (1 + Number(data?.invoice.vat ?? 0) / 100));
  }, [data, quantities]);

  async function submit() {
    if (!data || selected === 0) {
      setError('Vui lòng nhập số lượng trả cho ít nhất một sản phẩm.');
      return;
    }
    const lines = data.lines
      .map((line) => ({ detailId: line.id, quantity: quantities[line.id] ?? 0, max: Number(line.quantity) }))
      .filter((line) => line.quantity > 0);
    if (lines.some((line) => !Number.isInteger(line.quantity) || line.quantity > line.max)) {
      setError('Số lượng trả phải là số nguyên và không vượt số lượng đã bán.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const result = await api<{ result: { message: string } }>(`/api/invoices/outbound/${invoiceId}/returns`, {
        method: 'POST',
        body: JSON.stringify({
          updatedAt: data.invoice.updatedAt,
          lines: lines.map(({ detailId, quantity }) => ({ detailId, quantity }))
        })
      });
      onReturned(result.result.message);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Không thể trả hàng.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="drawer-backdrop" role="presentation">
      <section className="invoice-return-modal" role="dialog" aria-modal="true" aria-labelledby="outbound-return-title">
        <header className="drawer-header"><h2 id="outbound-return-title">Khách hàng trả hàng</h2><button type="button" className="icon-button" onClick={onClose} disabled={saving} aria-label="Đóng"><CloseIcon /></button></header>
        {loading ? <div className="editor-loading"><span className="spinner" /> Đang tải…</div> : null}
        {data ? (
          <div className="return-layout">
            <section className="return-meta">
              <div><span>Hóa đơn</span><strong>{data.invoice.sku}</strong></div>
              <div><span>Khách hàng</span><strong>{data.invoice.partnerName || 'Không xác định'}</strong></div>
              <div><span>Dòng đã chọn</span><strong>{selected}</strong></div>
              <div><span>Giá trị giảm dự kiến</span><strong>{money(estimatedRefund)} ₫</strong></div>
            </section>
            <div className="return-lines-wrap">
              <table className="return-lines outbound-return-lines">
                <thead><tr><th>Sản phẩm / lô</th><th>Đã bán</th><th>ĐVT</th><th>Giá bán</th><th>CK %</th><th>Số lượng trả</th></tr></thead>
                <tbody>{data.lines.map((line) => (
                  <tr key={line.id}>
                    <td><strong>{line.productName}</strong><span>{productSecondaryText(line.productName, line.detailName, line.sku || 'Không có mã lô', line.isGift ? 'Quà tặng' : '')}</span></td>
                    <td className="align-right">{money(line.quantity)}</td>
                    <td>{line.unitName || '—'}</td>
                    <td className="align-right">{money(line.price)} ₫</td>
                    <td className="align-right">{money(line.discountPercent ?? 0)}%</td>
                    <td><input type="number" min={0} max={line.quantity} step={1} value={quantities[line.id] ?? 0} disabled={saving} aria-label={`Số lượng trả ${line.productName}`} onChange={(event) => { const quantity = Math.max(0, Math.min(Number(line.quantity), Math.trunc(Number(event.target.value) || 0))); setQuantities((current) => ({ ...current, [line.id]: quantity })); setError(''); }} /></td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
            {error ? <div className="return-error" role="alert">{error}</div> : null}
          </div>
        ) : error ? <div className="detail-error" role="alert">{error}</div> : null}
        <footer className="drawer-footer"><button type="button" className="secondary-action" onClick={onClose} disabled={saving}>Hủy</button><button type="button" className="primary-action" onClick={() => void submit()} disabled={saving || loading || selected === 0}>{saving ? <span className="button-spinner" /> : <ReturnIcon />} {saving ? 'Đang xử lý…' : 'Xác nhận trả hàng'}</button></footer>
      </section>
    </div>
  );
}
